import { SlidersHorizontal } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { FilterButton } from '@//components/FilterButton';
import FilterList from '@//components/FilterList';
import type { Market } from '@//config/domain-locale-map';
import { resolveQuickFilters } from '@//utils/vehicles-search/market-specifc-filters';
import type { EnrichedElasticSearchResponseFacet } from '@//utils/vehicles-search/useEnrichedFacets';
import useUpdateVehiclesSearchUrl from '@//utils/vehicles-search/useUpdateVehiclesSearchUrl';
import useVehicleSearchParams from '@//utils/vehicles-search/useVehicleSearchParams';
import { sanitizeParams } from '@//utils/vehicles-search/utils';
import { getFilterActiveState } from '../getFilterActiveState';

const CategoryFilterModal = lazy(() => import('../FilterModals/CategoryFilterModal'));
const FilterModal = lazy(() => import('../FilterModals/FilterModal'));

interface QuickFiltersProps {
  enrichedFacets?: EnrichedElasticSearchResponseFacet[];
  totalHits?: number;
  loading: boolean;
  market: Market;
  rootUrl: string;
}

const QuickFilters = ({
  enrichedFacets,
  totalHits,
  loading,
  market,
  rootUrl,
}: QuickFiltersProps) => {
  const t = useTranslations('utils.search');
  const searchParams = useVehicleSearchParams(rootUrl);
  const [selectedFacetId, setSelectedFacetId] = useState<string>();
  const [categroryFilterModal, setCategroryFilterModal] = useState(false);
  const updateUrl = useUpdateVehiclesSearchUrl(rootUrl);

  const onCloseSelectedFacetId = useCallback(() => setSelectedFacetId(undefined), []);
  const onCloseCategoryFilterModal = useCallback(() => setCategroryFilterModal(false), []);

  const facet = enrichedFacets?.find((facet) => facet.id === selectedFacetId);

  const quickFilters = useMemo(() => resolveQuickFilters(market), [market]);

  const quickEnrichedFacets = useMemo(
    () =>
      enrichedFacets
        ?.filter((facet) => quickFilters.includes(facet.id))
        .sort((a, b) => quickFilters.indexOf(a.id) - quickFilters.indexOf(b.id)),
    [enrichedFacets, quickFilters],
  );

  const onToggleChange = useCallback(
    (facet: EnrichedElasticSearchResponseFacet, checked: boolean) => {
      const next = new URLSearchParams(searchParams);
      const existing = next.getAll(facet.id);
      if (checked) {
        next.append(facet.id, 'true');
      } else {
        next.delete(facet.id);
        existing.filter((v) => v !== 'true').forEach((v) => next.append(facet.id, v));
      }
      updateUrl(sanitizeParams(next));
    },
    [searchParams, updateUrl],
  );

  return (
    <>
      <Suspense fallback={null}>
        <CategoryFilterModal
          isOpen={categroryFilterModal}
          enrichedFacets={enrichedFacets}
          onClose={onCloseCategoryFilterModal}
          totalHits={totalHits}
          loading={loading}
          rootUrl={rootUrl}
        />
      </Suspense>
      <Suspense fallback={null}>
        <FilterModal
          facet={facet}
          isOpen={!!facet}
          onClose={onCloseSelectedFacetId}
          totalHits={totalHits}
          loading={loading}
          rootUrl={rootUrl}
        />
      </Suspense>
      <FilterList ariaLabel={t('filters.ariaLabel')}>
        <FilterList.Item>
          <FilterButton onClick={() => setCategroryFilterModal(true)} testId="open-filters-button">
            <SlidersHorizontal aria-hidden="true" />
            {t('filters.allFilters')}
          </FilterButton>
        </FilterList.Item>
        {quickEnrichedFacets?.map((facet) => {
          if (facet.type === 'toggle') {
            const { isActive } = getFilterActiveState(facet, searchParams);
            return (
              <FilterList.Item key={facet.id}>
                <div>
                  <input
                    type="checkbox"
                    id={`category-filter-modal-input-${facet.id}`}
                    value="true"
                    checked={isActive}
                    onChange={(e) => onToggleChange(facet, e.target.checked)}
                  />
                  <label htmlFor={`category-filter-modal-input-${facet.id}`}>
                    {facet.displayName}
                  </label>
                </div>
              </FilterList.Item>
            );
          }

          const { isActive, count } = getFilterActiveState(facet, searchParams);

          return (
            <FilterList.Item key={facet.id}>
              <FilterButton
                onClick={() => setSelectedFacetId(facet.id)}
                count={isActive ? count.toString() : undefined}
                countAriaLabel={`(${t('filters.activeFiltersCount', { count })})`}
              >
                {facet.displayName}
              </FilterButton>
            </FilterList.Item>
          );
        })}
      </FilterList>
    </>
  );
};

export default QuickFilters;
